import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import moment from 'moment';
import "moment-timezone";
import FeatherIcon from "feather-icons-react";
import { Scrollbars } from 'react-custom-scrollbars';
import {
    Input,
    InputNumber,
    DatePicker,
    Select,
    Row,
    Col,
    Drawer,
    Space,
    message
} from "antd";
import {
    HolderOutlined,
    FunctionOutlined,
} from "@ant-design/icons";
import {
    DndContext,
    closestCenter,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../../../components/buttons/buttons";
import { SaveIcon, CancelIcon, ManageColumnIcon } from '../../../../components/Icons/Icons';
import { nameOnChangeValidator } from '../../../../components/utilities/utilities';

const COLUMN_TYPES = [
    { label: "Text", value: "text" },
    { label: "Number", value: "number" },
    { label: "Currency", value: "currency" },
    { label: "Date", value: "date" },
    { label: "Formula", value: "formula" },
];
const FORMULA_RESULT_TYPES = [
    { label: "Currency", value: "currency" },
    { label: "Number", value: "number" },
];
const FORMULA_RESULT_TYPES2 = [
    { label: "Formula", value: "formula" },
    { label: "Number", value: "number" },
];
const FORMULA_RESULT_TYPES1 = [
    { label: "Currency", value: "currency" },
    { label: "Formula", value: "formula" },
];
const FORMULA_ALLOWED_FIELD_TYPES = ["number", "currency", "formula"];
const generateId = () => `col_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
const generateFieldId = (columns) => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const usedIndexes = columns.map((col) => {
        const letter = col.fieldId?.charAt(0);
        return letters.indexOf(letter);
    });
    const maxIndex = usedIndexes.length
        ? Math.max(...usedIndexes)
        : -1;
    return `${letters[maxIndex + 1] || "X"}1`;
};
const applyOperator = (op, b, a) => {
    if (op === "+") return a + b;
    if (op === "-") return a - b;
    if (op === "*") return a * b;
    if (op === "/") return b !== 0 ? a / b : 0;
    return 0;
};
const precedence = (op) => {
    if (op === "+" || op === "-") return 1;
    if (op === "*" || op === "/") return 2;
    return 0;
};
const parseExpression = (expr) => {
    const values = [];
    const ops = [];
    let i = 0;
    const reduce = () => {
        const op = ops.pop();
        const b = values.pop();
        const a = values.pop();
        values.push(applyOperator(op, b, a));
    };
    while (i < expr.length) {
        const ch = expr[i];
        if (ch === " ") {
            i += 1;
        } else if ((ch >= "0" && ch <= "9") || ch === ".") {
            let numStr = "";
            while (
                i < expr.length &&
                ((expr[i] >= "0" && expr[i] <= "9") || expr[i] === ".")
            ) {
                numStr += expr[i];
                i += 1;
            }
            values.push(parseFloat(numStr));
        } else if (ch === "(") {
            ops.push(ch);
            i += 1;
        } else if (ch === ")") {
            while (ops.length > 0 && ops[ops.length - 1] !== "(") {
                reduce();
            }
            ops.pop();
            i += 1;
        } else if (
            ch === "+" || ch === "-" ||
            ch === "*" || ch === "/"
        ) {
            const isUnaryMinus =
                ch === "-" &&
                (i === 0 || expr[i - 1] === "(");
            if (isUnaryMinus) {
                let numStr = "-";
                i += 1;
                while (
                    i < expr.length &&
                    ((expr[i] >= "0" && expr[i] <= "9") || expr[i] === ".")
                ) {
                    numStr += expr[i];
                    i += 1;
                }
                values.push(parseFloat(numStr));
            } else {
                while (
                    ops.length > 0 &&
                    ops[ops.length - 1] !== "(" &&
                    precedence(ops[ops.length - 1]) >= precedence(ch)
                ) {
                    reduce();
                }
                ops.push(ch);
                i += 1;
            }
        } else {
            i += 1;
        }
    }
    while (ops.length > 0) {
        reduce();
    }
    return values[0] || 0;
};
const FORMULA_TOKEN_REGEX = /\s+|[A-Za-z]+[0-9]+|\d+(?:\.\d+)?|[+\-*/()]/g;
const tokenizeFormula = (formula) => {
    const tokens = [];
    let lastIndex = 0;
    let match;
    FORMULA_TOKEN_REGEX.lastIndex = 0;
    match = FORMULA_TOKEN_REGEX.exec(formula);
    while (match !== null) {
        if (match.index !== lastIndex) {
            tokens.push({
                type: "INVALID",
                value: formula.slice(lastIndex, match.index),
            });
        }

        lastIndex = FORMULA_TOKEN_REGEX.lastIndex;

        const value = match[0];

        if (/^\s+$/.test(value)) {
            // whitespace is allowed but isn't a token itself
        } else if (/^[A-Za-z]+[0-9]+$/.test(value)) {
            tokens.push({ type: "FIELD", value });
        } else if (/^\d+(\.\d+)?$/.test(value)) {
            tokens.push({ type: "NUMBER", value });
        } else if (value === "(") {
            tokens.push({ type: "LPAREN", value });
        } else if (value === ")") {
            tokens.push({ type: "RPAREN", value });
        } else {
            tokens.push({ type: "OP", value });
        }

        match = FORMULA_TOKEN_REGEX.exec(formula);
    }
    if (lastIndex !== formula.length) {
        tokens.push({
            type: "INVALID",
            value: formula.slice(lastIndex),
        });
    }
    return tokens;
};

const evaluateFormula = (formula, columns, row) => {
    try {
        const tokens = tokenizeFormula(formula);
        if (tokens.some((t) => t.type === "INVALID")) return 0;
        let expression = "";
        tokens.forEach((tok) => {
            if (tok.type === "FIELD") {
                const col = columns.find((c) => c.fieldId === tok.value);
                const value = col ? Number(row[col.id]) || 0 : 0;
                expression += String(value);
            } else {
                expression += tok.value;
            }
        });
        if (!/^[0-9+\-*/().]+$/.test(expression)) return 0;
        return parseExpression(expression) || 0;
    } catch (e) {
        return 0;
    }
};
const validateFormulaFields = (formula, columns, selfFieldId) => {
    if (!formula || !formula.trim()) return "";

    const tokens = tokenizeFormula(formula);

    const invalidTokens = tokens.filter((t) => t.type === "INVALID");
    if (invalidTokens.length > 0) {
        const bad = [...new Set(invalidTokens.map((t) => t.value.trim()).filter(Boolean))];
        return `Formula contains invalid character(s): ${bad.join(", ")}`;
    }

    const availableFields = columns.map((c) => c.fieldId);
    const fieldTokens = tokens.filter((t) => t.type === "FIELD");

    const unknownFields = [
        ...new Set(
            fieldTokens
                .map((t) => t.value)
                .filter((field) => !availableFields.includes(field))
        ),
    ];
    if (unknownFields.length > 0) {
        return `Invalid field(s): ${unknownFields.join(", ")}`;
    }

    if (selfFieldId) {
        const selfReferenced = fieldTokens.some((t) => t.value === selfFieldId);
        if (selfReferenced) {
            return `Formula for ${selfFieldId} can't reference itself (${selfFieldId})`;
        }
    }

    const wrongTypeFields = [
        ...new Set(
            fieldTokens
                .map((t) => t.value)
                .filter((field) => {
                    const col = columns.find((c) => c.fieldId === field);
                    return col && !FORMULA_ALLOWED_FIELD_TYPES.includes(col.type);
                })
        ),
    ];
    if (wrongTypeFields.length > 0) {
        return `Field(s) must be Number/Currency: ${wrongTypeFields.join(", ")}`;
    }

    // Structural check: formula := term (operator term)*
    // term := ['-'] (FIELD | NUMBER | '(' formula ')')
    let parenDepth = 0;
    let expectTerm = true; // true => next token must start a term
    for (let i = 0; i < tokens.length; i += 1) {
        const tok = tokens[i];
        if (expectTerm) {
            if (tok.type === "FIELD" || tok.type === "NUMBER") {
                expectTerm = false;
            } else if (tok.type === "LPAREN") {
                parenDepth += 1;
            } else if (tok.type === "OP" && tok.value === "-") {
                // unary minus, still expecting a term
            } else {
                return `Invalid formula near "${tok.value}" - expected a field or number`;
            }
        } else if (tok.type === "OP") {
            expectTerm = true;
        } else if (tok.type === "RPAREN") {
            parenDepth -= 1;
            if (parenDepth < 0) {
                return 'Invalid formula: unmatched ")"';
            }
        } else {
            return `Invalid formula near "${tok.value}" - expected an operator (+ - * /)`;
        }
    }
    if (expectTerm) {
        return "Invalid formula: cannot end with an operator";
    }
    if (parenDepth !== 0) {
        return 'Invalid formula: unmatched "("';
    }
    if (fieldTokens.length === 0) {
        return "Formula must reference at least one field (e.g. B1)";
    }

    return "";
};
const getFormulaDependencies = (fieldId, columns) => {
    const dependentColumns = columns.filter((col) => {
        if (col.type !== "formula") return false;
        if (!col.formula) return false;
        const regex = new RegExp(`\\b${fieldId}\\b`);
        return regex.test(col.formula);
    });
    return dependentColumns;
};
const SortableColumnRow = ({
    column,
    errors,
    updateColumn,
    removeColumn,
    draftColumns,
    setColumnErrors,
}) => {
    const scrollRef = useRef(null);
    const getFieldPopupContainer = (triggerNode) =>
        scrollRef.current?.view ||
        triggerNode.closest('.ant-drawer-content') ||
        triggerNode.closest('.ant-modal-content') ||
        document.body;
    const isLocked = column.id === "item" || column.id === "total";
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: column.id, disabled: isLocked, });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        padding: "12px 14px",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        marginBottom: 10,
        background: "#fff",
    };
    const isFormula = column.type === "formula";
    return (
        <div ref={setNodeRef} style={style}>
            <Row
                gutter={[10, 0]}
                align="middle"
                wrap={false}
                style={{ minHeight: 44 }}
            >
                <Col flex="28px">
                    {isLocked ? <></> :
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: isLocked ? "not-allowed" : "grab",
                                opacity: isLocked ? 0.5 : 1,
                                color: "#bbb",
                            }}
                            {...attributes}
                            {...listeners}
                        >
                            <HolderOutlined style={{ fontSize: 16 }} />
                        </span>}
                </Col>
                <Col flex="36px">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#666",
                                background: "#f5f5f5",
                                borderRadius: 4,
                                padding: "2px 6px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                                lineHeight: 1.2,
                            }}
                        >
                            {column.fieldId}
                        </div>
                    </div>
                </Col>
                <Col flex="1">
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            minHeight: 44,
                        }}
                    >
                        <Input
                            status={errors?.title ? "error" : ""}
                            value={column.title}
                            placeholder="Column Name"
                            style={{ borderRadius: 6 }}
                            onChange={(e) => {
                                const values = e.target.value;
                                const error = nameOnChangeValidator(values, 50);
                                if (error) {
                                    message.error(error);
                                    return;
                                }
                                updateColumn(column.id, "title", values);
                                setColumnErrors((prev) => ({
                                    ...prev,
                                    [column.id]: {
                                        ...prev[column.id],
                                        title: values.trim()
                                            ? ""
                                            : "Column name is required",
                                    },
                                }));
                            }}
                        />
                        {errors?.title && (
                            <div
                                style={{
                                    color: "#ff4d4f",
                                    fontSize: 12,
                                    marginTop: 4,
                                }}
                            >
                                {errors.title}
                            </div>
                        )}</div>
                </Col>
                <Col flex="150px">
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                            minHeight: 44,
                        }}
                    >
                        <Select getPopupContainer={getFieldPopupContainer}
                            disabled={!column.isEditable}
                            value={column.type}
                            style={{ width: "100%" }}
                            options={
                                column?.id === "qty"
                                    ? FORMULA_RESULT_TYPES2
                                    : (column?.id === "price" ? FORMULA_RESULT_TYPES1
                                        : COLUMN_TYPES)
                            }
                            onChange={(value) => {
                                updateColumn(column.id, "type", value);
                                if (value !== "formula") {
                                    updateColumn(column.id, "formula", "");
                                    updateColumn(column.id, "formulaType", "");
                                } else {
                                    updateColumn(column.id, "formulaType", "number");
                                }
                                const dependents = getFormulaDependencies(
                                    column.fieldId,
                                    draftColumns
                                ).filter((dep) => dep.id !== column.id);
                                if (dependents.length === 0) return;
                                const simulatedColumns = draftColumns.map((col) =>
                                    col.id === column.id ? { ...col, type: value } : col
                                );

                                setColumnErrors((prev) => {
                                    const updated = { ...prev };
                                    dependents.forEach((dep) => {
                                        const formulaError = FORMULA_ALLOWED_FIELD_TYPES.includes(value)
                                            ? validateFormulaFields(dep.formula, simulatedColumns, dep.fieldId)
                                            : `Field "${column.fieldId}" is no longer Number/Currency`;
                                        updated[dep.id] = {
                                            ...updated[dep.id],
                                            formula: formulaError,
                                        };
                                    });
                                    return updated;
                                });
                            }}
                        /></div>
                </Col>
                <Col flex="32px">
                    {column?.isHide && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                minHeight: 44,
                            }}
                        >
                            {column.hidden ? (
                                <FeatherIcon
                                    icon="eye-off"
                                    size={18}
                                    className="customrow-action-icon"
                                    onClick={() =>
                                        updateColumn(
                                            column.id,
                                            "hidden",
                                            !column.hidden
                                        )
                                    }
                                />
                            ) : (
                                <FeatherIcon
                                    icon="eye"
                                    size={18}
                                    className="customrow-action-icon"
                                    onClick={() =>
                                        updateColumn(
                                            column.id,
                                            "hidden",
                                            !column.hidden
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}
                </Col>
                <Col flex="32px">
                    {column?.idDelete && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                minHeight: 44,
                            }}
                        >
                            <FeatherIcon
                                icon="trash-2"
                                size={18}
                                className="fetcherDelete-Icon"
                                onClick={() => removeColumn(column.id)}
                            />
                        </div>
                    )}
                </Col>
            </Row>
            {isFormula && (
                <Row
                    gutter={[10, 0]}
                    align="middle"
                    wrap={false}
                    style={{
                        marginTop: 10,
                        minHeight: 44,
                    }}
                >
                    <Col flex="70px" />
                    <Col flex="1">
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                minHeight: 44,
                            }}
                        >
                            <Input
                                disabled={!column.isEditable}
                                status={errors?.formula ? "error" : ""}
                                prefix={
                                    <FunctionOutlined
                                        style={{
                                            color: "#888",
                                            fontSize: 14,
                                            marginRight: 4,
                                        }}
                                    />
                                }
                                placeholder="e.g. B1 * C1 (fields + numbers only, joined by + - * /)"
                                value={column.formula}
                                style={{ borderRadius: 6, textTransform: "uppercase" }}
                                onChange={(e) => {
                                    const values = e.target.value.toUpperCase();
                                    updateColumn(column.id, "formula", values);
                                    const error = validateFormulaFields(values, draftColumns, column.fieldId);
                                    setColumnErrors((prev) => ({
                                        ...prev,
                                        [column.id]: {
                                            ...prev[column.id],
                                            formula: error,
                                        },
                                    }));
                                }}
                            />
                            {errors?.formula && (
                                <div
                                    style={{
                                        color: "#ff4d4f",
                                        fontSize: 12,
                                        marginTop: 4,
                                    }}
                                >
                                    {errors.formula}
                                </div>
                            )}
                        </div>
                    </Col>
                    <Col flex="150px">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                minHeight: 44,
                            }}
                        >
                            <Select getPopupContainer={getFieldPopupContainer}
                                disabled={!column.isEditable}
                                value={column.formulaType}
                                style={{ width: "100%" }}
                                options={FORMULA_RESULT_TYPES}
                                onChange={(value) =>
                                    updateColumn(
                                        column.id,
                                        "formulaType",
                                        value
                                    )
                                }
                            /></div>
                    </Col>
                    <Col flex="64px" />
                </Row>
            )}
        </div>
    );
};
SortableColumnRow.propTypes = {
    errors: PropTypes.object,
    column: PropTypes.object,
    updateColumn: PropTypes.func,
    removeColumn: PropTypes.func,
    draftColumns: PropTypes.array,
    setColumnErrors: PropTypes.func,
};
const CustomInvoiceBuilder = ({
    currencySettings,
    onChange,
    items, profile,
    itemTableHeader
}) => {
    const [columnDrawer, setColumnDrawer] = useState(false);
    const [draftColumns, setDraftColumns] = useState([]);
    const emitChanges = (updatedSchema, updatedRows) => {
        if (onChange) onChange({ schema: updatedSchema, rows: updatedRows });
    };
    const recalculateFormulas = (updatedRows, updatedColumns = itemTableHeader) =>
        updatedRows.map((row) => {
            const updatedRow = { ...row };
            updatedColumns.forEach((col) => {
                if (col.type === "formula" && col.formula) {
                    updatedRow[col.id] = evaluateFormula(
                        col.formula,
                        updatedColumns,
                        updatedRow
                    );
                }
            });
            return updatedRow;
        });
    const openColumnDrawer = () => {
        setDraftColumns(JSON.parse(JSON.stringify(itemTableHeader)));
        setColumnDrawer(true);
    };
    const addDraftColumn = () => {
        const newColumn = {
            id: generateId(),
            fieldId: generateFieldId(draftColumns),
            title: "New Column",
            type: "text",
            formulaType: "",
            formula: "",
            width: 180,
            hidden: false,
            idDelete: true,
            isHide: true,
            isEditable: true,
        };
        setDraftColumns(prev => {
            const totalIndex = prev.findIndex(col => col.id === "total");
            if (totalIndex === -1) {
                return [...prev, newColumn];
            }
            const updated = [...prev];
            updated.splice(totalIndex, 0, newColumn);
            return updated;
        });
        message.success("New column added");
    };
    const updateDraftColumn = (columnId, field, value) =>
        setDraftColumns((prev) =>
            prev.map((col) =>
                col.id === columnId ? { ...col, [field]: value } : col
            )
        );
    const removeDraftColumn = (columnId) => {
        const columnToDelete = draftColumns.find(
            (col) => col.id === columnId
        )
        if (!columnToDelete) return;
        const dependencies = getFormulaDependencies(
            columnToDelete.fieldId,
            draftColumns
        ).filter((col) => col.id !== columnId);
        if (dependencies.length > 0) {
            const usedIn = dependencies
                .map((col) => col.title)
                .join(", ");
            message.error(
                `Cannot delete "${columnToDelete.title}". Used in formula: ${usedIn}`
            );
            return;
        }
        setDraftColumns((prev) =>
            prev.filter((col) => col.id !== columnId)
        );
    };
    const [columnErrors, setColumnErrors] = useState({});
    const validateColumns = () => {
        const errors = {};
        draftColumns.forEach((col) => {
            if (!col.title || !col.title.trim()) {
                errors[col.id] = {
                    ...errors[col.id],
                    title: "Column name is required",
                };
            }
            if (col.type === "formula") {
                if (!col.formula || !col.formula.trim()) {
                    errors[col.id] = {
                        ...errors[col.id],
                        formula: "Formula is required",
                    };
                } else {
                    const formulaError = validateFormulaFields(col.formula, draftColumns, col.fieldId);
                    if (formulaError) {
                        errors[col.id] = {
                            ...errors[col.id],
                            formula: formulaError,
                        };
                    }
                }
            }
        });
        setColumnErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const defaultValueForType = (type) =>
        type === "number" || type === "currency" || type === "formula" ? 0 : "";
    const saveColumns = () => {
        const isValid = validateColumns();
        if (!isValid) return;
        const oldColumnsById = {};
        itemTableHeader.forEach((col) => { oldColumnsById[col.id] = col; });
        const oldIds = itemTableHeader.map((c) => c.id);
        const newIds = draftColumns.map((c) => c.id);
        const deletedIds = oldIds.filter((id) => !newIds.includes(id));
        const addedColumns = draftColumns.filter((col) => !oldIds.includes(col.id));
        const typeChangedColumns = draftColumns.filter((col) => {
            const oldCol = oldColumnsById[col.id];
            return oldCol && oldCol.type !== col.type;
        });
        let updatedRows = items.map((row) => {
            const cloned = { ...row };
            deletedIds.forEach((id) => { delete cloned[id]; });
            addedColumns.forEach((col) => {
                cloned[col.id] = defaultValueForType(col.type);
            });
            typeChangedColumns.forEach((col) => {
                cloned[col.id] = defaultValueForType(col.type);
            });
            return cloned;
        });
        updatedRows = recalculateFormulas(updatedRows, draftColumns);
        emitChanges(draftColumns, updatedRows);
        setColumnDrawer(false);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = draftColumns.findIndex((c) => c.id === active.id);
        const newIndex = draftColumns.findIndex((c) => c.id === over.id);
        if (draftColumns[oldIndex]?.id === "item" || draftColumns[oldIndex]?.id === "total") { return }
        if (draftColumns[newIndex]?.id === "item" || draftColumns[newIndex]?.id === "total") { return }
        const updated = arrayMove(draftColumns, oldIndex, newIndex);
        const itemCol = updated.find(c => c.id === "item");
        const withoutItem = updated.filter(c => c.id !== "item");
        const totalCol = withoutItem.find(c => c.id === "total");
        const middleCols = withoutItem.filter(c => c.id !== "total");
        setDraftColumns([
            itemCol,
            ...middleCols,
            totalCol,
        ]);
    };
    const addRow = () => {
        const emptyRow = {};
        itemTableHeader.forEach((col) => {
            emptyRow[col.id] = col.id === "qty" ? 1 : (col.type === "number" || col.type === "currency" || col.type === "formula") ? 0 : "";
        });
        const updatedRows = recalculateFormulas([...items, emptyRow]);
        emitChanges(itemTableHeader, updatedRows);
    };
    const removeRow = (index) => {
        const updatedRows = [...items];
        updatedRows.splice(index, 1);
        emitChanges(itemTableHeader, updatedRows);
    };
    const RequiredDateFormating = (dateString, profileData) => {
        const { dateFormate } = profileData;
        if (dateString) {
            const parsedDate = moment(dateString, dateFormate || "YYYY-MM-DD")
            const formattedDate = parsedDate.format("YYYY-MM-DD");
            return formattedDate;
        }
        return null;
    };
    const updateCell = (rowIndex, columnId, value, type, profile) => {
        let updatedRows = [...items];
        if (type === "date") {
            const date = RequiredDateFormating(value, profile)
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                [columnId]: date,
            };
        }
        else {
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                [columnId]: value,
            };
        }
        updatedRows = recalculateFormulas(updatedRows);
        emitChanges(itemTableHeader, updatedRows);
    };
    const renderCell = (col, row, rowIndex) => {
        if (col.type === "formula") {
            const isCurrency =
                col.formulaType === "currency";
            return (
                <div key={col.id} className={`customInvoice-field customInvoice-col-id-${col.id}`}>
                    {isCurrency ? (
                        <InputNumber
                            bordered={false}
                            readOnly
                            style={{ width: "100%" }}
                            value={row[col.id]}
                            formatter={(value) =>
                                `${currencySettings?.symbol || "₹"} ${value}`
                            }
                        />
                    ) : (
                        <Input
                            bordered={false}
                            readOnly
                            value={row[col.id]}
                        />
                    )}
                </div>
            );
        }
        switch (col.type) {
            case "text":
                return (
                    <div key={col.id} className={`customInvoice-field customInvoice-col-id-${col.id}`}>
                        <Input
                            bordered={false}
                            placeholder={col.title}
                            value={row[col.id]}
                            onChange={(e) => {
                                const values = e.target.value;
                                const error = nameOnChangeValidator(values, 50);
                                if (error) {
                                    message.error(error);
                                    return;
                                }
                                updateCell(rowIndex, col.id, values)
                            }}
                        />
                    </div>
                );
            case "number":
                return (
                    <div key={col.id} className={`customInvoice-field customInvoice-col-id-${col.id}`}>
                        <Input
                            bordered={false}
                            style={{ width: "100%" }}
                            // maxLength={25}
                            value={row[col.id]}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                const decimalLimit = currencySettings?.decimalOfCurrency || 2;
                                const basicNumberCheck = /^\d*\.?\d*$/.test(inputValue);
                                if (inputValue?.toString()?.length > 25) {
                                    message.error('Maximum 25 characters allowed');
                                    return;
                                }
                                if (!basicNumberCheck) {
                                    message.error('Please enter a valid number');
                                    return;
                                }
                                if (inputValue === ".") {
                                    message.error('Please enter a valid number');
                                    return;
                                }
                                const decimalRegex = new RegExp(`^\\d+(\\.\\d{0,${decimalLimit}})?$`);
                                if (inputValue && !decimalRegex.test(inputValue)) {
                                    message.error(`Only up to ${decimalLimit} decimal place${decimalLimit > 1 ? 's' : ''} are allowed`);
                                    return;
                                }
                                updateCell(rowIndex, col.id, inputValue)
                            }}
                        />
                    </div>
                );
            case "currency":
                return (
                    <div key={col.id} className={`customInvoice-field customInvoice-col-id-${col.id}`}>
                        <Input prefix={currencySettings?.symbol}
                            bordered={false}
                            style={{ width: "100%" }}
                            // maxLength={25}
                            value={row[col.id]}
                            onChange={(e) => {
                                const inputValue = e.target.value;
                                const decimalLimit = currencySettings?.decimalOfCurrency || 2;
                                const basicNumberCheck = /^\d*\.?\d*$/.test(inputValue);
                                if (inputValue?.toString()?.length > 25) {
                                    message.error('Maximum 25 characters allowed');
                                    return;
                                }
                                if (!basicNumberCheck) {
                                    message.error('Please enter a valid number');
                                    return;
                                }
                                if (inputValue === ".") {
                                    message.error('Please enter a valid number');
                                    return;
                                }
                                const decimalRegex = new RegExp(`^\\d+(\\.\\d{0,${decimalLimit}})?$`);
                                if (inputValue && !decimalRegex.test(inputValue)) {
                                    message.error(`Only up to ${decimalLimit} decimal place${decimalLimit > 1 ? 's' : ''} are allowed`);
                                    return;
                                }
                                updateCell(rowIndex, col.id, inputValue)
                            }}
                        />
                    </div>
                );
            case "date":
                return (
                    <div key={col.id} className={`customInvoice-field customInvoice-col-id-${col.id}`}>
                        <DatePicker
                            bordered={false}
                            style={{ width: "100%" }}
                            format={profile.dateFormate ? profile.dateFormate : "YYYY-MM-DD"}
                            value={
                                row[col.id]
                                    ? moment(row[col.id], "YYYY-MM-DD")
                                    : null
                            }
                            onChange={(_, dateString) =>
                                updateCell(rowIndex, col.id, dateString, "date", profile)
                            }
                        />
                    </div>
                );
            default:
                return null;
        }
    };
    return (
        <>
            <div
                style={{
                    gap: 10,
                    marginBottom: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    className="button_outlined"
                    size="small"
                    icon={<ManageColumnIcon />}
                    onClick={openColumnDrawer}
                >
                    Manage Columns
                </Button>
                <Button
                    size="small"
                    className="button_colors"
                    onClick={addRow}
                >
                    <FeatherIcon icon="plus-circle" size={16} /> Add Row
                </Button>
            </div>
            <div className="customInvoice-builder" style={{ marginBottom: 20 }}>
                <div className="customInvoice-grid-header">
                    {itemTableHeader
                        .filter((col) => !col.hidden)
                        .map((col) => (
                            <div
                                key={col.id}
                                className={`customInvoice-header-cell customInvoice-col-${col.id}`}
                            >
                                <div>{col.title}</div>
                            </div>
                        ))}
                    <div className="customInvoice-header-actions">Actions</div>
                </div>
                <div className="customInvoice-grid-body">
                    {items.map((row, rowIndex) => (
                        <div key={rowIndex} className="customInvoice-row-card">
                            <div className="customInvoice-row-grid">
                                {itemTableHeader
                                    ?.filter((col) => !col.hidden)
                                    .map((col) =>
                                        renderCell(col, row, rowIndex)
                                    )}
                                <div className="customInvoice-row-actions">
                                    <FeatherIcon
                                        icon="arrow-up"
                                        size={18}
                                        className={`customrow-action-icon ${rowIndex === 0 ? "disabled" : ""}`}
                                        onClick={() => {
                                            if (rowIndex === 0) return;
                                            const updatedRows = [...items];
                                            [
                                                updatedRows[rowIndex - 1],
                                                updatedRows[rowIndex],
                                            ] = [
                                                    updatedRows[rowIndex],
                                                    updatedRows[rowIndex - 1],
                                                ];
                                            emitChanges(itemTableHeader, updatedRows);
                                        }}
                                    />
                                    <FeatherIcon
                                        icon="arrow-down"
                                        size={18}
                                        className={`customrow-action-icon ${rowIndex === items.length - 1 ? "disabled" : ""}`}
                                        onClick={() => {
                                            if (rowIndex === items.length - 1) return;
                                            const updatedRows = [...items];
                                            [
                                                updatedRows[rowIndex + 1],
                                                updatedRows[rowIndex],
                                            ] = [
                                                    updatedRows[rowIndex],
                                                    updatedRows[rowIndex + 1],
                                                ];
                                            emitChanges(itemTableHeader, updatedRows);
                                        }}
                                    />
                                    <FeatherIcon
                                        icon="trash-2"
                                        size={18}
                                        className="fetcherDelete-Icon"
                                        onClick={() => removeRow(rowIndex)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <Drawer className='Drawer-Header-Pages'
                bodyStyle={{ padding: 0 }} closable={false}
                placement="right"
                title="Customize Columns & Formulas"
                open={columnDrawer}
                width="50%"
                extra={
                    <Space>
                        <div className='Hover-Refresh2 back-hover1 able-style'
                            role="button"
                            tabIndex={0} title='Close'
                            onClick={() => setColumnDrawer(false)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    setColumnDrawer(false)
                                }
                            }}
                            aria-label='Close'
                        > <CancelIcon width={24} height={24} /></div>
                    </Space>
                }
                footer={[
                    <div className="form_action_button">
                        <Button
                            className="gray-button" size="small"
                            onClick={() => setColumnDrawer(false)}
                        >
                            <CancelIcon height={25} width={25} />Cancel
                        </Button>
                        <Button size="small" htmlType="submit" className="button_colors"
                            onClick={() => saveColumns()}>
                            <SaveIcon height={25} width={25} />Save
                        </Button>
                    </div>]
                }
            >
                <Scrollbars autoHide autoHideTimeout={500} autoHideDuration={200}>
                    <div style={{ margin: 40 }}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                            }}
                        >
                            <div style={{ marginBottom: 14, textAlign: "right" }}>
                                <Button size="small" className="button_colors" disabled={draftColumns.length >= 8}
                                    onClick={addDraftColumn}
                                >
                                    <FeatherIcon icon="plus-circle" size={16} /> Add New Column
                                </Button>
                            </div>
                            <div>
                                <DndContext
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={draftColumns.map((c) => c.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {draftColumns.map((col) => (
                                            <SortableColumnRow
                                                errors={columnErrors[col.id] || {}}
                                                draftColumns={draftColumns}
                                                setColumnErrors={setColumnErrors}
                                                key={col.id}
                                                column={col}
                                                updateColumn={updateDraftColumn}
                                                removeColumn={removeDraftColumn}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                        </div>
                    </div>
                </Scrollbars>
            </Drawer>
        </>
    );
};

CustomInvoiceBuilder.propTypes = {
    currencySettings: PropTypes.object,
    onChange: PropTypes.func,
    items: PropTypes.array,
    itemTableHeader: PropTypes.array,
    profile: PropTypes.object,
};

export default CustomInvoiceBuilder;